import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { useBulkPutProjects } from "../api";

import {
  ALL_PROJECT_FIELDS,
  buildProjectsToImport,
  detectAmazonTracker,
  mapHeaderToField,
  type BulkRow,
  type ProjectField,
} from "./bulkImportHelpers";
import styles from "./BulkImportRoute.module.css";

// Ports legacy `BulkImportPage`. Excel/CSV in -> Project rows out
// via POST /projects/put. Single sheet only (the legacy heuristic
// picks "Building tracking" for Amazon trackers, else sheet[0]).
type TrackerType = "amazon" | "generic" | null;

interface ParsedSheet {
  fileName: string;
  headers: string[];
  rows: unknown[][];
  trackerType: TrackerType;
  mapping: Record<number, ProjectField>;
}

interface ImportResultState {
  ok_ids: string[];
  errors: { project_id: string; error: string }[];
}

export const BulkImportRoute = () => {
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ImportResultState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const mutation = useBulkPutProjects();

  const projectsToImport: BulkRow[] = useMemo(() => {
    if (!parsed) return [];
    return buildProjectsToImport(
      parsed.rows,
      parsed.mapping,
      parsed.trackerType,
      parsed.fileName,
    );
  }, [parsed]);

  const reset = () => {
    setParsed(null);
    setParseError(null);
    setProgress({ done: 0, total: 0 });
    setResult(null);
  };

  const handleFile = async (file: File | null | undefined) => {
    if (!file) return;
    setParseError(null);
    setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName =
        wb.SheetNames.find((n) => n === "Building tracking") ||
        wb.SheetNames[0];
      if (!sheetName) throw new Error("Workbook has no sheets.");
      const ws = wb.Sheets[sheetName];
      const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, {
        header: 1,
        defval: "",
      });
      if (grid.length < 2) {
        throw new Error("Workbook has no data rows.");
      }
      const headers = (grid[0] as unknown[]).map((h) =>
        String(h ?? "").trim(),
      );
      const auto: Record<number, ProjectField> = {};
      headers.forEach((h, i) => {
        const field = mapHeaderToField(h);
        if (field) auto[i] = field;
      });
      const isAmazon = detectAmazonTracker(headers);

      // Strict row filter: only keep rows with a value in the
      // column that will become project_id (Airport Code for Amazon
      // trackers; otherwise whichever column maps to project_id,
      // falling back to address).
      const projectIdCol = Object.entries(auto).find(
        ([, f]) => f === "project_id",
      )?.[0];
      const airportCodeCol = Object.entries(auto).find(
        ([, f]) => f === "airport_code",
      )?.[0];
      const addressCol = Object.entries(auto).find(
        ([, f]) => f === "address",
      )?.[0];
      const keyCol = projectIdCol ?? airportCodeCol ?? addressCol;
      const dataRows = grid.slice(1).filter((row) => {
        if (keyCol !== undefined) {
          const v = (row as unknown[])[Number(keyCol)];
          return v != null && String(v).trim() !== "";
        }
        return (row as unknown[]).some(
          (c) => c !== "" && c != null && String(c).trim() !== "",
        );
      }) as unknown[][];

      setParsed({
        fileName: file.name,
        headers,
        rows: dataRows,
        trackerType: isAmazon ? "amazon" : "generic",
        mapping: auto,
      });
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
      setParsed(null);
    }
  };

  const handleImport = async () => {
    if (mutation.isLoading || projectsToImport.length === 0) return;
    setProgress({ done: 0, total: projectsToImport.length });
    try {
      const resp = await mutation.mutateAsync({
        projects: projectsToImport,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      setResult({ ok_ids: resp.ok_ids, errors: resp.errors });
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  // ---- file drop ----
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  if (result) {
    return (
      <div className={styles.page}>
        <section className={styles.section}>
          <h2 className={styles.h2}>Bulk import — done</h2>
          <div className={styles.resultSummary}>
            <strong>{result.ok_ids.length}</strong> project
            {result.ok_ids.length === 1 ? "" : "s"} imported.
            {result.errors.length > 0 && (
              <>
                {" "}
                <strong className={styles.errCount}>
                  {result.errors.length}
                </strong>{" "}
                row{result.errors.length === 1 ? "" : "s"} failed.
              </>
            )}
          </div>
          {result.ok_ids.length > 0 && (
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                Successfully imported ({result.ok_ids.length})
              </div>
              <div className={styles.idList}>
                {result.ok_ids.map((id) => (
                  <code key={id} className={styles.idChip}>
                    {id}
                  </code>
                ))}
              </div>
            </div>
          )}
          {result.errors.length > 0 && (
            <div className={styles.field}>
              <div className={styles.fieldLabel}>Errors</div>
              <ul className={styles.errList}>
                {result.errors.map((e) => (
                  <li key={e.project_id}>
                    <code>{e.project_id}</code>: {e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={styles.actions}>
            <Link to="/operations" className={styles.linkBtn}>
              Open Operations
            </Link>
            <button type="button" onClick={reset} className={styles.resetBtn}>
              Import another file
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <section className={styles.section}>
        <h2 className={styles.h2}>Bulk import</h2>
        <p className={styles.lede}>
          Upload your multi-site tracker (Excel or CSV). The platform creates
          one project per row in Operations. Column headers are matched
          against the Project schema by name (common synonyms auto-detected).
          Amazon "Building tracking" workbooks get their project_id from the
          Airport Code column.
        </p>

        {!parsed && (
          <div
            className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ""}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <div className={styles.dropzoneIcon}>+</div>
            <div className={styles.dropzonePrimary}>
              Drop tracker workbook here
            </div>
            <div className={styles.dropzoneSecondary}>
              or click to browse — .xlsx, .xls, .csv (first sheet only)
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className={styles.fileInput}
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
          </div>
        )}

        {parseError && (
          <div className={styles.err}>
            Couldn't read the workbook: {parseError}
          </div>
        )}

        {parsed && (
          <PreviewPanel
            parsed={parsed}
            projectsToImport={projectsToImport}
            importing={mutation.isLoading}
            progress={progress}
            onMappingChange={(mapping) =>
              setParsed((prev) => (prev ? { ...prev, mapping } : prev))
            }
            onImport={handleImport}
            onCancel={reset}
          />
        )}
      </section>
    </div>
  );
};

interface PreviewProps {
  parsed: ParsedSheet;
  projectsToImport: BulkRow[];
  importing: boolean;
  progress: { done: number; total: number };
  onMappingChange: (mapping: Record<number, ProjectField>) => void;
  onImport: () => void;
  onCancel: () => void;
}

const PreviewPanel = ({
  parsed,
  projectsToImport,
  importing,
  progress,
  onMappingChange,
  onImport,
  onCancel,
}: PreviewProps) => {
  const updateMapping = (colIdx: number, field: ProjectField | "") => {
    const next = { ...parsed.mapping };
    if (field === "") {
      delete next[colIdx];
    } else {
      // Clear any other column mapped to the same field (each
      // ProjectField may be sourced from at most one column).
      for (const [k, v] of Object.entries(next)) {
        if (v === field) delete next[Number(k)];
      }
      next[colIdx] = field;
    }
    onMappingChange(next);
  };

  const mappedCount = Object.keys(parsed.mapping).length;
  const skippedCount = parsed.headers.length - mappedCount;

  return (
    <div className={styles.previewWrap}>
      <div className={styles.previewHead}>
        <div>
          <div className={styles.fileName}>{parsed.fileName}</div>
          <div className={styles.fileMeta}>
            {parsed.rows.length} data row
            {parsed.rows.length === 1 ? "" : "s"}
            {" · "}
            {mappedCount} column{mappedCount === 1 ? "" : "s"} mapped
            {skippedCount > 0 && `, ${skippedCount} skipped`}
            {parsed.trackerType === "amazon" && (
              <span className={styles.trackerTag}>Amazon tracker</span>
            )}
          </div>
        </div>
        <div className={styles.summary}>
          Will import <strong>{projectsToImport.length}</strong> project
          {projectsToImport.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className={styles.mappingTable}>
        <div className={styles.mappingHead}>
          <span>Column header</span>
          <span>Maps to</span>
        </div>
        {parsed.headers.map((h, i) => (
          <div key={i} className={styles.mappingRow}>
            <span className={styles.mappingHeader}>{h || "(blank)"}</span>
            <select
              value={parsed.mapping[i] ?? ""}
              onChange={(e) =>
                updateMapping(i, e.target.value as ProjectField | "")
              }
              className={styles.mappingSelect}
              disabled={importing}
            >
              <option value="">— skip this column —</option>
              {ALL_PROJECT_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {importing && (
        <div className={styles.progress}>
          Importing {progress.done} / {progress.total}…
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onCancel}
          className={styles.cancelBtn}
          disabled={importing}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onImport}
          className={styles.importBtn}
          disabled={importing || projectsToImport.length === 0}
        >
          {importing
            ? `Importing… (${progress.done}/${progress.total})`
            : `Import ${projectsToImport.length} project${projectsToImport.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
};
